import { destroy, stopSubmit } from 'redux-form'
import * as Effects from 'redux-saga/effects'
import { put, select } from 'redux-saga/effects'
import {
  eventWasUpdated,
  IEventWasUpdated,
  IRequestUpdateEvent,
} from '../../actions/bookingCalendar/event/requestUpdateEvent'
import selectReservationTab from '../../actions/bookingCalendar/reservationModal/selectReservationTab'
import {
  toggleReservationModalIsSaving,
  toggleUpdateReservationModal,
} from '../../actions/bookingCalendar/reservationModal/toggleReservationModal'
import { callFetch } from '../../actions/fetch'
import closeModal from '../../actions/Modals/closeModal'
import { requestSynchronization } from '../../actions/synchronize/synchronize'
import {
  fireToastError,
  fireToastSuccess,
} from '../../actions/toasts/fireToast'
import { ICreateReservationFormValues } from '../../containers/Form/CreateReservation/CreateReservation'
import { Modals } from '../../containers/Modals/ModalContainer'
import { database } from '../../database'
import i18n from '../../i18n'
import requestUpdate from '../../models/Event/updateEvent'
import { addOffsetDate } from '../../models/License/mappers/mapResponseToLicense'
import { UpdateLimitType } from '../../models/Limit'
import { EventActions } from '../../reducers/bookingCalendar/actionNames'
import { getUpdateModalReservationId } from '../../reducers/bookingCalendar/selectors/getModalOpen'
import fetchActionsName, { ActionNames } from '../../utils/FetchActionsName'
import { getEvents } from '../events/loadEventsForList'
import { LimitResponse, LimitType, loadLimits } from '../userAccount/loadLimits'
import updateLimits from '../userAccount/updateLimits'
import { logEvent } from './createEvent'
import { getUserId } from '../../reducers/userAccount/selectors/userAccount'
import { getUserCurrency } from '../../reducers/userObject/selectors/object'
import { findRoomById } from '../../reducers/bookingCalendar/selectors/rooms'
import getInvoice from '../../reducers/invoice/getInvoice'

export function* updateReservation(
  eventId: string,
  event: ICreateReservationFormValues,
) {
  const actionNames = fetchActionsName(ActionNames.UpdateEvent)
  const [{ calendarId, service }] = yield Effects.all([
    Effects.take(actionNames.output),
    Effects.put(
      callFetch(actionNames.input, actionNames.output, requestUpdate),
    ),
  ])

  const events = yield Effects.call(service, calendarId, eventId, event)
  const userId = yield select(getUserId)
  const currency = yield select(getUserCurrency)

  for (const event of events) {
    if ('gtag' in window) {
      ;(window as any).gtag('event', 'conversion', {
        send_to: 'AW-927657230/zzsYCNCzipADEI7aq7oD',
      })
    }
    const room = yield select(state =>
      findRoomById(state, { roomId: event.roomId }),
    )
    const invoice = yield select(getInvoice)
    logEvent(userId, event, room, currency, true, invoice)
    yield Effects.put(eventWasUpdated(event))
  }
}

function* updateEvent(action: IRequestUpdateEvent) {
  yield Effects.put(toggleReservationModalIsSaving(true))
  const response = yield* loadLimits(LimitType.Edit)
  if (response === LimitResponse.Rejected) {
    yield Effects.put(toggleReservationModalIsSaving(false))
    return
  }
  try {
    const eventId = action.eventId
      ? action.eventId
      : yield Effects.select(getUpdateModalReservationId)

    yield* updateReservation(eventId, action.event)

    yield Effects.put(toggleUpdateReservationModal(null, false))

    yield Effects.put(destroy('update_reservation'))
    yield Effects.put(closeModal(Modals.ReservationDeleteModal))
    yield Effects.put(
      fireToastSuccess(
        i18n.t('reservation_update_succes'),
        undefined,
        undefined,
        'reservation_update_succes',
      ),
    )
    yield Effects.put(destroy('update_reservation'))
    if (!action.toastOnSyncNeeded) {
      yield Effects.put(closeModal(Modals.ReservationDeleteModal))
    }
    yield* updateLimits(UpdateLimitType.Edit)
  } catch (e) {
    console.log(e)
    if (e.message === '409') {
      yield Effects.put(requestSynchronization())
      yield Effects.put(selectReservationTab(0))
      if (action.toastOnSyncNeeded) {
        yield put(
          fireToastError(
            i18n.t('errStatus_5'),
            undefined,
            undefined,
            'reservation_update_error',
          ),
        )
      }
    } else if (e.message === 'Period is not free') {
      yield put(
        fireToastError(
          i18n.t('z677_period_is_not_free'),
          undefined,
          undefined,
          'reservation_update_error',
        ),
      )
    } else {
      console.error(e)
      yield put(
        fireToastError(
          i18n.t('error_server_response_2'),
          undefined,
          undefined,
          'reservation_update_error',
        ),
      )
    }
    yield Effects.put(stopSubmit('update_reservation', e))
  } finally {
    yield Effects.put(toggleReservationModalIsSaving(false))
  }
}

function* putEventIntoDb(action: IEventWasUpdated) {
  if (action.putToDb === false) {
    return ''
  }
  const { event } = action

  yield database.events.update(action.event.id, {
    ...event,
    bookingPrice: null,
    dateToPay: event.dateToPay
      ? addOffsetDate(event.dateToPay).getTime()
      : undefined,
    dateToPayAfterStart: event.dateToPayAfterStart
      ? addOffsetDate(event.dateToPayAfterStart).getTime()
      : undefined,
    deposit: null,
    finishDate: addOffsetDate(event.finishDate).getTime(),
    localTax: null,
    paymentOnPlace: null,
    paymentOnPlaceDate: event.paymentOnPlaceDate
      ? addOffsetDate(event.paymentOnPlaceDate).getTime()
      : undefined,
    price: null,
    pricePerDay: null,
    provision: null,
    provisionDate: event.provisionDate
      ? addOffsetDate(event.provisionDate).getTime()
      : undefined,
    savedColor: event.customColor ? event.customColor.rgb().toString() : null,
    startDate: addOffsetDate(event.startDate).getTime(),
  })

  yield* getEvents()
}

export default function*() {
  yield Effects.takeLatest(EventActions.RequestUpdateEvent, updateEvent)
  yield Effects.takeEvery(EventActions.EventWasUpdated, putEventIntoDb)
}
